var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2019, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { css, ClassNames } from '@emotion/react';
import ReactModal from 'react-modal';
import { useClickEvent } from '../../hooks/useClickEvent';
import Image from '../Image';
import Headline from '../Headline';
import Body from '../Body';
import ButtonGroup from '../ButtonGroup';
import CloseButton from '../CloseButton';
import { cx, spacing } from '../../styles/style-mixins';
import { CircuitError } from '../../util/errors';
const TRANSITION_DURATION = 200;
const closeButtonStyles = (theme) => css `
  position: absolute;
  top: ${theme.spacings.byte};
  right: ${theme.spacings.byte};

  ${theme.mq.kilo} {
    top: ${theme.spacings.mega};
    right: ${theme.spacings.mega};
  }
`;
const imageStyles = (theme) => css `
  max-width: 232px;
  height: 120px;
  object-fit: contain;
  margin: 0 auto ${theme.spacings.giga};
`;
const svgStyles = css `
  height: 100%;
  width: 100%;
`;
function NotificationImage({ image }) {
    if (!image) {
        return null;
    }
    if ('svg' in image) {
        const Svg = image.svg;
        const isDecorative = !image.alt;
        return (_jsx("div", Object.assign({ css: imageStyles }, { children: _jsx(Svg, Object.assign({ css: svgStyles }, (isDecorative
                ? { 'aria-hidden': true }
                : { 'aria-label': image.alt, 'role': 'img' }))) })));
    }
    return _jsx(Image, Object.assign({}, image, { css: imageStyles }));
}
// Prevent the headline from being overlapped by the close button
const noImageStyles = (hasImage) => !hasImage &&
    css `
    max-width: 80%;
    margin-right: auto;
    margin-left: auto;
  `;
/**
 * Circuit UI's wrapper component for ReactModal.
 * http://reactcommunity.org/react-modal/accessibility/#aria
 */
export const NotificationModal = (_a) => {
    var { image, headline, body, actions, onClose, closeButtonLabel, preventClose = false, tracking, className } = _a, props = __rest(_a, ["image", "headline", "body", "actions", "onClose", "closeButtonLabel", "preventClose", "tracking", "className"]);
    if (process.env.NODE_ENV !== 'production' && className) {
        throw new CircuitError('NotificationModal', 'Custom styles are not supported by this component. If your use case requires custom styles, please open an issue at https://github.com/sumup-oss/circuit-ui.');
    }
    const handleClose = useClickEvent(onClose, tracking, 'modal-close');
    return (_jsx(ClassNames, { children: ({ css: cssString, theme }) => {
            // React Modal styles
            // https://reactcommunity.org/react-modal/styles/classes/
            const styles = {
                base: cssString `
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: calc(100vw - ${theme.spacings.peta} * 2);
            max-width: 420px;
            max-height: calc(100vh - ${theme.spacings.mega} * 2);
            outline: none;
            background-color: var(--cui-bg-elevated);
            border-radius: ${theme.borderRadius.mega};
            padding: ${theme.spacings.giga};
            text-align: center;
            opacity: 0;
            transition: opacity ${TRANSITION_DURATION}ms ease-in-out;
            overflow-y: auto;

            ${theme.mq.untilKilo} {
              -webkit-overflow-scrolling: touch;
            }
          `,
                afterOpen: cssString `
           opacity: 1;
          `,
                beforeClose: cssString `
            opacity: 0;
        `,
            };
            const overlayStyles = {
                base: cssString `
            position: fixed;
            top: 0;
            left: 0;
            bottom: 0;
            right: 0;
            opacity: 0;
            transition: opacity ${TRANSITION_DURATION}ms ease-in-out;
            background: var(--cui-bg-overlay);
            z-index: ${theme.zIndex.modal};

            ${theme.mq.kilo} {
              -webkit-overflow-scrolling: touch;
              overflow-y: auto;
            }
          `,
                afterOpen: cssString `
            opacity: 1;
          `,
                beforeClose: cssString `
            opacity: 0;
          `,
            };
            const reactModalProps = Object.assign({ className: styles, overlayClassName: overlayStyles, onRequestClose: handleClose, closeTimeoutMS: TRANSITION_DURATION, shouldCloseOnOverlayClick: !preventClose, shouldCloseOnEsc: !preventClose }, props);
            function wrapOnClick(onClick) {
                return (event) => {
                    handleClose === null || handleClose === void 0 ? void 0 : handleClose(event);
                    onClick === null || onClick === void 0 ? void 0 : onClick(event);
                };
            }
            const hasImage = Boolean(image);
            return (_jsxs(ReactModal, Object.assign({}, reactModalProps, { children: [!preventClose && closeButtonLabel && (_jsx(CloseButton, { onClick: onClose, label: closeButtonLabel, css: closeButtonStyles })), _jsx(NotificationImage, { image: image }), _jsx(Headline, Object.assign({ as: "h2", size: "three", css: cx(spacing({ bottom: 'byte' }), noImageStyles(hasImage)) }, { children: headline })), body && _jsx(Body, { children: body }), actions && (_jsx(ButtonGroup, { actions: {
                            primary: Object.assign(Object.assign({}, actions.primary), { onClick: wrapOnClick(actions.primary.onClick) }),
                            secondary: actions.secondary && Object.assign(Object.assign({}, actions.secondary), { onClick: wrapOnClick(actions.secondary.onClick) }),
                        }, css: spacing({ top: 'giga' }) }))] })));
        } }));
};
NotificationModal.TRANSITION_DURATION = TRANSITION_DURATION;
